import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  seoConfig
} from './config/seoConfig';

/**
 * SEO Component for managing page metadata
 *
 * @param {Object} props
 * @param {string} props.title - Override page title
 * @param {string} props.description - Override page description
 * @param {string} props.image - Override OG image URL
 * @param {string} props.type - OG type (website, article, product)
 * @param {Object|Array} props.schema - JSON-LD structured data
 * @param {boolean} props.noindex - Whether to noindex the page
 * @param {Object} props.article - Article-specific metadata for blog posts
 */
const SEO = ({
  title,
  description,
  image,
  type = 'website',
  schema,
  noindex = false,
  article = null,
}) => {
  const location = useLocation();

  // Extract language and path from URL
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentLang = SUPPORTED_LANGUAGES.includes(pathParts[0]) ? pathParts[0] : DEFAULT_LANGUAGE;
  const pathWithoutLang = pathParts.slice(1).join('/');

  // Determine route key for config lookup
  // Handle special cases like campervan/sun-catchers
  let routeKey = pathWithoutLang || 'home';

  // Get config-based metadata or use provided overrides
  const configMeta = seoConfig[routeKey]?.[currentLang] || seoConfig.home?.[currentLang] || {};
  const pageTitle = title || configMeta.title || SITE_NAME;
  const pageDescription = description || configMeta.description || '';
  const pageImage = image || DEFAULT_IMAGE;

  // Build canonical URL
  const canonicalPath = pathWithoutLang ? `/${pathWithoutLang}` : '';
  const canonicalUrl = `${SITE_URL}/${currentLang}${canonicalPath}`;

  // Build hreflang URLs for all supported languages
  const hreflangUrls = SUPPORTED_LANGUAGES.map(lang => ({
    lang,
    url: `${SITE_URL}/${lang}${canonicalPath}`,
  }));

  // Get locale for OG tags
  const getOGLocale = (lang) => {
    const localeMap = {
      en: 'en_US',
      pt: 'pt_PT',
      es: 'es_ES',
      it: 'it_IT',
    };
    return localeMap[lang] || 'en_US';
  };

  // Prepare schema as array
  const schemaArray = schema
    ? (Array.isArray(schema) ? schema.filter(Boolean) : [schema])
    : [];

  return (
    <Helmet>
      {/* HTML Language */}
      <html lang={currentLang} />

      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Tags for Multi-language Support */}
      {hreflangUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/${DEFAULT_LANGUAGE}${canonicalPath}`} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={getOGLocale(currentLang)} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Article-specific meta tags */}
      {article && (
        <>
          {article.publishedDate && (
            <meta property="article:published_time" content={article.publishedDate} />
          )}
          {article.modifiedDate && (
            <meta property="article:modified_time" content={article.modifiedDate} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
        </>
      )}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Structured Data (JSON-LD) */}
      {schemaArray.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(schemaArray.length === 1 ? schemaArray[0] : schemaArray)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
